/**
 * Transaction-page Trends v2 adapter.
 *
 * FE-1 owns data lifecycle and local bucket granularity only. FE-2 supplies the
 * resolved presentation through `renderPayload` and later attaches this adapter
 * at the existing `insights-trends-tab` host boundary.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  getTransactionTrendsWindowDays,
  resolveTransactionTrendsGranularity,
  useTransactionTrends,
} from '@/hooks/useTransactionTrends';
import { stableStringify } from '@/hooks/useInsightsCache';
import type { FilterState } from '@/lib/filters';
import type {
  ResolvedTransactionTrendsGranularity,
  TransactionTrendsPayload,
} from '@/lib/resolverContracts';

export interface TrendsV2ResolvedState {
  data: TransactionTrendsPayload;
  filterState: FilterState;
  granularity: ResolvedTransactionTrendsGranularity;
  onGranularityChange: (granularity: ResolvedTransactionTrendsGranularity) => void;
  refetch: () => void;
}

export interface TrendsV2AdapterProps {
  /** Active floating-bar state, already synchronized with the transaction URL. */
  filterState: FilterState;
  /** User-preference timezone forwarded to the authoritative endpoint when present. */
  timezone?: string;
  /** Optional presentation seam used by the live V2 skin while this adapter owns lifecycle. */
  renderPayload?: (state: TrendsV2ResolvedState) => ReactNode;
}

interface LocalGranularityState {
  windowKey: string;
  value: ResolvedTransactionTrendsGranularity;
}

export function TrendsV2Adapter({
  filterState,
  timezone,
  renderPayload,
}: TrendsV2AdapterProps) {
  const windowKey = stableStringify({
    period: filterState.period,
    dateFrom: filterState.dateFrom,
    dateTo: filterState.dateTo,
    timezone,
  });
  const initialGranularity = useMemo(
    () =>
      resolveTransactionTrendsGranularity(
        getTransactionTrendsWindowDays(filterState, timezone),
      ),
    // `windowKey` is the content address for the floating-bar window inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [windowKey],
  );
  const [localGranularity, setLocalGranularity] = useState<LocalGranularityState>(() => ({
    windowKey,
    value: initialGranularity,
  }));
  const granularity =
    localGranularity.windowKey === windowKey
      ? localGranularity.value
      : initialGranularity;

  const handleGranularityChange = useCallback(
    (nextGranularity: ResolvedTransactionTrendsGranularity) => {
      setLocalGranularity({ windowKey, value: nextGranularity });
    },
    [windowKey],
  );

  const { data, loading, error, refetch } = useTransactionTrends(
    filterState,
    granularity,
    timezone,
  );

  // [T-TRENDS-REFACTOR-v1 / FE-2 Selector stability] Every branch below renders INSIDE
  // a stable `<div data-testid="trends-v2-root">` host so the AC1a mount-contract
  // assertion (`insights-trends-tab → trends-v2-root`) passes regardless of state.
  // State-specific testids (loading/error/empty) remain on the inner element so QA can
  // distinguish them without coupling to the root.
  let body: ReactNode;
  if (loading) {
    body = (
      <div
        data-testid="trends-v2-loading"
        role="status"
        aria-label="Loading Trends insights"
        className="w-full min-w-0 space-y-4 overflow-hidden"
      >
        <Skeleton className="min-h-[120px] w-full" />
        <Skeleton className="min-h-[240px] w-full" />
        <Skeleton className="min-h-[280px] w-full" />
      </div>
    );
  } else if (error) {
    body = (
      <Alert
        data-testid="trends-v2-error"
        variant="destructive"
        aria-label="Trends insights error"
        className="w-full min-w-0 overflow-hidden"
      >
        <AlertTitle>Unable to load Trends insights</AlertTitle>
        <AlertDescription className="break-words">{error}</AlertDescription>
        <Button
          type="button"
          variant="outline"
          data-testid="trends-v2-retry"
          aria-label="Retry Trends insights"
          className="mt-4 min-h-[44px] min-w-[44px]"
          onClick={refetch}
        >
          Retry
        </Button>
      </Alert>
    );
  } else if (!data || data.meta.transactionCount === 0) {
    body = (
      <div
        data-testid="trends-v2-empty"
        role="status"
        aria-label="Trends insights empty state"
        className="w-full min-w-0 overflow-hidden rounded-xl border bg-card p-4 text-card-foreground"
      >
        <p className="break-words text-sm text-muted-foreground">
          No transactions in this window.
        </p>
      </div>
    );
  } else {
    body = (
      <div
        role="region"
        aria-label="Resolved Trends insights"
        data-granularity={granularity}
        data-resolver-status={data.meta.resolverStatus}
        className="w-full min-w-0 overflow-hidden"
      >
        {renderPayload ? (
          renderPayload({
            data,
            filterState,
            granularity,
            onGranularityChange: handleGranularityChange,
            refetch,
          })
        ) : (
          <p role="status" className="sr-only">
            Trends insights loaded for {data.meta.transactionCount} transactions.
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid="trends-v2-root"
      className="w-full min-w-0 overflow-hidden"
    >
      {body}
    </div>
  );
}

export default TrendsV2Adapter;
